import axios from 'axios'
import { FormApi } from 'final-form'
import { get, isEmpty, last, noop } from 'lodash'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { getDefaultHeaders } from '../../../utils/getDefaultHeaders'
import SteppingBar from '../../SteppingBar'
import { AS_URI, COPYPASTE, QRCODE } from '../constant'
import { TFaData, TRecoveryCode, TSubmitQRCode } from '../types'
import ChooseMethodStep from './ChooseMethodStep'
import RecoveryCodeStep from './RecoveryCodeStep'
import ValidationStep from './ValidationStep'

type TContentTwoFa = {
  FaData?: TFaData
  baseFaUrl?: string
  twoFaEnabled?: boolean
  faMethod?: string | null
  setFaMethod?: (val: string) => void
  loadingValidation?: boolean
  successValidation?: boolean | null
  recoveryCodes?: TRecoveryCode[]
  submitQRCode?: TSubmitQRCode
  current?: number
  setCurrent?: (val: number) => void
  setLoadingValidation?: (val: boolean) => void
  recentlyEnabledTwoFaOrRefreshed: boolean
}

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const MethodWrapper = styled.div`
  padding-top: 10px;
  display: flex;
  flex-direction: column;

  @media (max-width: 767px) {
    padding-top: 0px;
    & button {
      max-height: 50px;
      width: 100%;
    }
  }
`

const ContentTwoFa = (props: TContentTwoFa): React.ReactElement => {
  const steps = [
    {
      position: 1,
      title: i18n.t(`2fa_select-method-step`, `Methode wählen`),
      titleShort: i18n.t(`2fa_select-method-step`, `Methode wählen`),
      icon: 'fas fa-mobile-alt',
    },
    {
      position: 2,
      title: i18n.t(`2fa_validation-step`, `Validierung`),
      titleShort: i18n.t(`2fa_validation-step`, `ValidaValidierungtion`),
      icon: 'far fa-shield-alt',
    },
    {
      position: 3,
      title: i18n.t(`2fa_recovery-code-step`, `Wiederherstellungscodes`),
      titleShort: i18n.t(`2fa_recovery-code-step-short`, `Wiederherstellung`),
      icon: 'far fa-shield-check',
    },
  ]

  const {
    FaData,
    baseFaUrl,
    twoFaEnabled,
    faMethod,
    setFaMethod = noop,
    loadingValidation,
    successValidation,
    recoveryCodes,
    submitQRCode = noop,
    current = 0,
    setCurrent = noop,
    setLoadingValidation = noop,
    recentlyEnabledTwoFaOrRefreshed = false,
  } = props

  const [form, setForm] = useState<FormApi>()

  const validateStep = (value: number): void => {
    if (isEmpty(faMethod) && value === 1) {
      setCurrent(0)
      return
    }

    if ((successValidation == null || !successValidation) && value === 2) {
      setCurrent(1)
      return
    }
    return undefined
  }

  const changeMethod = (index: number): void => {
    setCurrent(1)
    if (index === 0) {
      setFaMethod(QRCODE)
      return
    }
    if (index === 1) {
      setFaMethod(AS_URI)
      return
    }
    setFaMethod(COPYPASTE)
    return
  }

  const onSubmitQRCode = (val): void => {
    setLoadingValidation(true)
    axios
      .post(
        `${baseFaUrl}/api/2fa/confirm`,
        {
          '2fa_code': get(val, '2fa_code', '').replace(/\s+/g, ''),
        },
        {
          ...getDefaultHeaders(),
        },
      )
      .then(({ data }) => {
        console.log('got data', data)
        const { recovery_codes = [] } = data
        if (recovery_codes.length > 0) {
          submitQRCode({
            success: true,
            recovery_codes: recovery_codes,
          })
          return
        }
      })
      .catch((error) => {
        const { message = 'Error' } = error
        toast.error(message)
        submitQRCode({ success: false })
        form?.change('2fa_code', '')
      })
  }

  const Content = [
    !twoFaEnabled && (
      <ChooseMethodStep
        key="ChooseMethodStep"
        faData={FaData}
        changeMethod={changeMethod}
      />
    ),
    !twoFaEnabled && (
      <ValidationStep
        key="ValidationStep"
        faData={FaData}
        faMethod={faMethod}
        form={form}
        setForm={setForm}
        loadingValidation={loadingValidation}
        onSubmitQRCode={onSubmitQRCode}
      />
    ),
    recentlyEnabledTwoFaOrRefreshed ? (
      <RecoveryCodeStep key="RecoveryCodeStep" recoveryCodes={recoveryCodes} />
    ) : null,
  ].filter(Boolean)

  return (
    <ContentWrapper>
      {!twoFaEnabled && !isEmpty(FaData) && (
        <div className="pt-2">
          <SteppingBar
            disableVertical
            color="#1abc9c"
            current={current}
            steps={steps}
            onChange={(val) => setCurrent(val)}
            disableNext
            validateStep={validateStep}
          />
        </div>
      )}
      <MethodWrapper>{get(Content, current) || last(Content)}</MethodWrapper>
    </ContentWrapper>
  )
}

export default ContentTwoFa
